// ============================================================
// fondos/motor.js — EL MOTOR DE LOS FONDOS ANIMADOS: el único dueño de su ciclo de vida (DOM)
//
// P3 del plan del motor de fondos (`CLAUDE_RESUMEN_PLANES/2026-09-24_plan-p3-motor-efectos.md`
// §2.3). Hasta aquí cada fondo se gobernaba solo: su IntersectionObserver lo arrancaba y lo
// paraba, se re-medía con `window.resize` y nadie contaba cuántos animaban a la vez. Y cada
// superficie los creaba a su manera (el runtime, la vista previa del BG Studio, la de variantes),
// con parches para no dejar lienzos huérfanos (P1). Ahora:
//
//   · VERBOS. `reconciliar(raiz)` (el hook post-render, la vista previa de variantes, la timeline),
//     `previsualizar(section, borrador)` y `restaurar(section)` (el BG Studio) y `desmontar(raiz)`.
//     Nadie más crea ni destruye un fondo.
//   · FIRMA. Tipo + lo que recibe el efecto + color + polaridad. Si no cambia, el efecto no se
//     rehace: solo se re-aplica la capa (opacidad y overlay) y el velo. Se acaba el
//     parpadeo al abrir y cerrar el panel, y rehacer el fondo de la timeline sale gratis.
//   · OBSERVADORES ÚNICOS. Un IntersectionObserver (qué se ve), un ResizeObserver (re-mide sin
//     reiniciar: E6, el lienzo ya no se estira) y `visibilitychange` (pestaña oculta → todo quieto).
//   · PRESUPUESTO (V5). Como mucho 3 animando; primero la vista previa del panel, luego el de más
//     área visible (`fondos/planificador.js`). El resto, en un fotograma estático.
//   · BUCLE ÚNICO (F2b, E7). Los lienzos propios (`fondos/lienzo.js` + `fondos/efectos/`) no tienen
//     bucle: los pinta UN rAF del motor, solo mientras alguno anima, a 30 fps con corrección de fase
//     (`crearCadencia`) y con el `dt` real, así que su velocidad no depende de la pantalla ni de la
//     carga. Desde la F3 son 12 (también humo, mesh y blobs). Aurora, la única de CAPAS
//     (`fondos/capas.js`), anima en el compositor: el motor solo la reproduce y la congela; no entra
//     en el bucle.
//   · MOVIMIENTO REDUCIDO Y GAMA BAJA (E11). Fotograma estático, no una sección lisa; también en
//     el editor (el panel lo avisa).
//   · SIN ZOMBIS. Una sección que sale del documento se desmonta en el siguiente barrido, y un
//     efecto que se carga cuando su sección ya se rehízo no llega a crearse (se instancia DESPUÉS
//     de que llegue su módulo, y solo si su controlador sigue mandando).
//   · ERRORES AISLADOS. Un efecto que lanza se queda quieto y avisa una sola vez por tipo; no se
//     lleva por delante los fondos de las demás secciones.
//
// Estado: `WeakMap<section, Controlador>` + un `Set` de vivos (el WeakMap no se puede recorrer, y
// el barrido y el presupuesto lo necesitan). `estadoDelMotor()` lo resume para los arneses.
// ============================================================

import { configEfectiva } from './efectiva.js';
import { asegurarLayer, layerDe, vaciarLayer, capaDePintura, aplicarOpacidadDeFondo,
         applyOverlay } from './capa.js';
import { esTexturaDeFondo, pintarTexturaDeFondo } from './texturas.js';
import { esEfecto, cargarEfecto, opcionesDelEfecto } from './efectos/index.js';
import { montarLienzo } from './lienzo.js';
import { montarCapas, semillaDe } from './capas.js';
import { porId, alfaMaxDe, idVigente } from './catalogo.js';
import { elegirAnimados, crearCadencia, PRESUPUESTO, FPS } from './planificador.js';
import { aplicarVelo } from '../../utils/velo.js';
import { prefersLightweight, IS_TOUCH } from '../motion-tokens.js';

/** Umbrales de visibilidad: cada 10 % se replanifica (el presupuesto mira el área visible). */
const UMBRALES = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];

const porSeccion = new WeakMap();
const vivos = new Set();
const avisados = new Set();
let io = null;
let ro = null;
let enMarcha = false;
let planPendiente = false;
/** El bucle único de los lienzos: su cadencia y el rAF pendiente (null = parado). */
const cadencia = crearCadencia(FPS);
let bucle = null;

// ── Qué se sabe pintar ─────────────────────────────────────────────────────────────────────────

/**
 * ¿Es un fondo que el motor sabe pintar? Una textura o un efecto; con `hasOwnProperty` en los dos
 * registros: un id guardado como 'constructor' o 'toString' no pasa (llegó a abortar el bucle de
 * toda la página).
 */
export function esFondoConocido(id) {
    return esTexturaDeFondo(id) || esEfecto(id);
}

/**
 * ¿Están los fondos QUIETOS en este navegador? Con «reducir movimiento» o en un equipo de gama baja
 * cada fondo se queda en un fotograma estático, en el editor también. El BG Studio lo avisa.
 */
export function fondosQuietos() {
    return prefersLightweight();
}

// ── Verbos ─────────────────────────────────────────────────────────────────────────────────────

/**
 * Pone cada sección con fondo de `raiz` como dicen sus atributos (`data-bg-anim`, `data-bg-config`…).
 * Lo que no ha cambiado (misma firma) no se rehace.
 *
 * @param {HTMLElement|Document} [raiz] - ÁMBITO. Sin él, toda la página (el hook post-render).
 *   Con él, solo las secciones que cuelgan de ahí (y la propia raíz si es una sección): lo usan la
 *   vista previa de variantes, el BG Studio al Aplicar y la timeline.
 */
export function reconciliar(raiz) {
    _barrerHuerfanos();
    const ambito = _ambito(raiz);
    let secciones = Array.prototype.slice.call(ambito.querySelectorAll('section[data-bg-anim]'));
    // `querySelectorAll` NO devuelve la propia raíz: si el ámbito ES una sección con fondo, se añade.
    if (ambito !== document && ambito.matches && ambito.matches('section[data-bg-anim]')) {
        secciones = [ambito].concat(secciones);
    }
    secciones.forEach(function (section) {
        // Un fondo que falla no se lleva por delante los de las secciones siguientes.
        try { _reconciliarSeccion(section, undefined); }
        catch (err) { console.error('Error pintando el fondo animado:', err); }
    });
    // Lo montado en el ámbito cuya sección ya no pide fondo (le han quitado `data-bg-anim`).
    vivos.forEach(function (c) {
        if (!c.preview && _enAmbito(c.section, ambito) && !c.section.hasAttribute('data-bg-anim')) {
            _desmontar(c, { quitarLayer: true });
        }
    });
    _replanificar();
}

/**
 * Pinta en `section` un fondo que todavía NO está en sus atributos: el borrador del BG Studio.
 * Pasa por la MISMA config efectiva que el runtime (`configEfectiva(section, borrador)`), así que
 * lo que se ve al elegir es lo que queda aplicado y lo que se publica. La sección queda en «vista
 * previa» hasta `restaurar`: tiene prioridad en el presupuesto y ninguna reconciliación la pisa.
 *
 * @param {HTMLElement} section
 * @param {{tipo: string, config?: Object, modo?: string, acento?: string, tono?: string}} borrador
 *   con `tipo: 'none'` (o uno que no se sabe pintar) la sección se enseña SIN fondo.
 */
export function previsualizar(section, borrador) {
    if (!section || section.isConnected === false) return;
    _barrerHuerfanos();
    const b = borrador || { tipo: 'none' };
    if (!esFondoConocido(b.tipo)) {
        const c = porSeccion.get(section);
        if (c) _desmontar(c, { quitarLayer: true });
        const suelta = layerDe(section);
        if (suelta) suelta.remove();
        // Sin fondo animado, el velo es el de una sección sin él (solo si hay foto).
        aplicarVelo(section, { hayFondo: false });
        _replanificar();
        return;
    }
    const c = _reconciliarSeccion(section, b);
    if (c) {
        c.preview = true;
        c.borrador = b;
    }
    _replanificar();
}

/**
 * Devuelve `section` a SU fondo guardado (el de sus atributos, que el panel nunca toca). Es lo que
 * convierte ✕, Escape y el clic fuera del BG Studio en un «cancelar» de verdad. Si lo guardado
 * tiene la misma firma que lo previsualizado, no se rehace nada.
 */
export function restaurar(section) {
    if (!section || section.isConnected === false) return;
    _barrerHuerfanos();
    const c = porSeccion.get(section);
    if (c) {
        c.preview = false;
        c.borrador = undefined;
    }
    const tipo = idVigente(section.getAttribute('data-bg-anim'));
    if (!esFondoConocido(tipo)) {
        // Sin fondo guardado (o uno que no se sabe pintar): fuera la capa, y el velo vuelve a ser
        // el que dicen los atributos de la sección.
        if (c) _desmontar(c, { quitarLayer: true });
        const suelta = layerDe(section);
        if (suelta) suelta.remove();
        aplicarVelo(section);
        _replanificar();
        return;
    }
    _reconciliarSeccion(section, undefined);
    _replanificar();
}

/**
 * Desmonta los fondos de `raiz` (sin ella, todos) y, siempre, los de secciones que ya no están en
 * el documento. Deja sus capas vacías.
 */
export function desmontar(raiz) {
    const ambito = (raiz && typeof raiz.querySelectorAll === 'function') ? raiz : null;
    vivos.forEach(function (c) {
        if (c.section.isConnected === false || !ambito || _enAmbito(c.section, ambito)) {
            _desmontar(c, { vaciar: true });
        }
    });
    _replanificar();
}

/**
 * Resumen del estado, para los arneses (`window.__n3iEstadoMotor` en sus páginas) y la consola.
 * @returns {{vivos, animando, quietos, texturas, cargando, presupuesto, quietosPorEntorno, oculto, secciones}}
 */
export function estadoDelMotor() {
    let animando = 0;
    let quietos = 0;
    let texturas = 0;
    let cargando = 0;
    const secciones = [];
    vivos.forEach(function (c) {
        if (c.textura) texturas++;
        else if (!c.efecto) cargando++;
        else if (c.animando) animando++;
        else quietos++;
        secciones.push({
            tipo: c.tipo,
            tecnica: c.textura ? 'textura' : (c.efecto ? c.efecto.tecnica : 'cargando'),
            visible: c.visible,
            animando: c.animando,
            preview: c.preview,
            pintado: c.pintado,
            roto: !!c.roto,
        });
    });
    return {
        vivos: vivos.size,
        animando: animando,
        quietos: quietos,
        texturas: texturas,
        cargando: cargando,
        presupuesto: PRESUPUESTO,
        quietosPorEntorno: fondosQuietos(),
        oculto: typeof document !== 'undefined' && !!document.hidden,
        secciones: secciones,
    };
}

// ── Una sección ────────────────────────────────────────────────────────────────────────────────

/**
 * Deja `section` como pide su config efectiva (con el borrador de la vista previa si lo hay).
 * @returns {Object|null} su controlador, o null si no lleva fondo
 */
function _reconciliarSeccion(section, borrador) {
    const c = porSeccion.get(section);
    // Una sección en vista previa conserva su borrador aunque la reconcilie otro (un hook post-render
    // que no la ha sustituido): solo el panel, con `restaurar`, la devuelve a lo guardado.
    const override = borrador !== undefined ? borrador : (c && c.preview ? c.borrador : undefined);
    const ef = configEfectiva(section, override);
    if (!esFondoConocido(ef.tipo)) {
        // Un id que el front no sabe pintar ('noise', 'glow'…: los hay en sitios reales) no se toca.
        if (c) _desmontar(c, { quitarLayer: true });
        return null;
    }
    const firma = _firma(ef);
    if (c && c.firma === firma && c.layer.parentNode === section) {
        _aplicarCapa(c, ef);
        aplicarVelo(section, { hayFondo: true });
        return c;
    }
    if (c) _desmontar(c, {});
    return _montar(section, ef, firma);
}

/** Lo que obliga a rehacer el efecto: tipo, lo que recibe (su suavidad incluida), color y
 *  polaridad. La opacidad y el overlay NO: son de la capa y se re-aplican sin rehacer nada. */
function _firma(ef) {
    const opciones = esTexturaDeFondo(ef.tipo) ? {} : opcionesDelEfecto(ef.tipo, ef.config);
    return JSON.stringify([ef.tipo, ef.color1, !!ef.isDark, opciones]);
}

function _montar(section, ef, firma) {
    const layer = asegurarLayer(section);
    // VELO DE LEGIBILIDAD: el `::after` de la sección, que el contrato de capas de `site.css` deja
    // POR ENCIMA de este fondo (`utils/velo.js`). Se aplica aquí, donde de verdad se crea la capa.
    aplicarVelo(section, { hayFondo: true });

    const c = {
        section: section,
        layer: layer,
        tipo: ef.tipo,
        firma: firma,
        ef: ef,
        textura: esTexturaDeFondo(ef.tipo),
        efecto: null,
        visible: false,
        animando: false,
        pintado: false,
        preview: false,
        borrador: undefined,
        medida: null,
        destruido: false,
    };
    porSeccion.set(section, c);
    vivos.add(c);

    if (c.textura) {
        pintarTexturaDeFondo(capaDePintura(layer, ef.tipo), ef.tipo, ef.color1, ef.isDark);
        c.pintado = true;
        _aplicarCapa(c, ef);
        return c;
    }

    _arrancar();
    if (io) io.observe(section);
    else c.visible = true;                    // sin IntersectionObserver: se da por visible

    cargarEfecto(ef.tipo).then(function (efecto) {
        // Llegó tarde: mientras se cargaba el módulo, otra pasada rehízo esta sección (o salió del
        // documento). No se instancia nada, así que no queda ningún lienzo huérfano que destruir.
        if (!_vigente(c)) return;
        try {
            const params = opcionesDelEfecto(c.tipo, c.ef.config);
            if (efecto.tecnica === 'lienzo') {
                const f = porId(c.tipo);
                c.efecto = montarLienzo(efecto.efecto, capaDePintura(layer, c.tipo), {
                    c1: c.ef.color1, oscuro: c.ef.isDark, params: params, tactil: IS_TOUCH,
                    alfa: alfaMaxDe(c.tipo, c.ef.isDark),
                    seccion: section, interaccion: !!(f && f.interaccion),
                });
            } else {
                c.efecto = montarCapas(efecto.efecto, capaDePintura(layer, c.tipo), {
                    c1: c.ef.color1, oscuro: c.ef.isDark, params: params, tactil: IS_TOUCH,
                    alfa: alfaMaxDe(c.tipo, c.ef.isDark), semilla: semillaDe(section.id + '|' + c.tipo),
                });
            }
        } catch (err) {
            _avisar(c.tipo, err);
            return;
        }
        c.medida = _medida(c);
        if (ro) ro.observe(section);
        // La capa, con la config MÁS RECIENTE: la vista previa pudo moverla mientras llegaba el módulo.
        _aplicarCapa(c, c.ef);
        _replanificar();
    }).catch(function (err) { _avisar(c.tipo, err); });

    return c;
}

/**
 * Opacidad y overlay: lo que NO rehace el efecto. Deja la capa entera como dice `ef` (sin opacidad
 * propia vuelve al 100 %, sin overlay lo quita): la vista previa lo mueve y `restaurar` lo devuelve a
 * lo guardado sin tocar el efecto. La SUAVIDAD (`blur`) no está aquí: la interpreta el efecto (E10),
 * así que es parte de su firma y rehacerlo es cambiarla.
 */
function _aplicarCapa(c, ef) {
    c.ef = ef;
    const config = ef.config;
    // A la capa que PINTA, nunca al layer: dentro vive el overlay combinado (`fondos/capa.js`).
    aplicarOpacidadDeFondo(c.layer, config.opacity);
    // Overlay combinado: solo sobre un fondo animado ya montado (las texturas no llevan overlay).
    // El id y su color salen de la config EFECTIVA, ya normalizados contra el registro que los pinta.
    if (!c.efecto) return;
    const overlayEl = applyOverlay(c.layer, ef.overlay, ef.overlayColor, ef.isDark);
    // Su opacidad es un mando APARTE (`overlayOpacity`), nunca la del fondo.
    if (overlayEl && ef.overlayOpacity !== undefined) overlayEl.style.opacity = ef.overlayOpacity / 100;
}

function _desmontar(c, opciones) {
    if (c.destruido) return;
    c.destruido = true;
    vivos.delete(c);
    if (porSeccion.get(c.section) === c) porSeccion.delete(c.section);
    if (io) io.unobserve(c.section);
    if (ro) ro.unobserve(c.section);
    if (c.efecto) {
        const efecto = c.efecto;
        c.efecto = null;
        _protegido(c, function () { efecto.destruir(); });
    }
    c.animando = false;
    const o = opciones || {};
    if (o.quitarLayer) { if (c.layer.parentNode) c.layer.remove(); }
    else if (o.vaciar) vaciarLayer(c.layer);
}

/** ¿Sigue mandando este controlador sobre su sección, y sigue su sección en el documento? */
function _vigente(c) {
    return !c.destruido && porSeccion.get(c.section) === c && c.section.isConnected !== false
        && c.layer.parentNode === c.section;
}

/** Barrido de huérfanos: una sección que salió del documento no la pausa ni la destruye nadie más. */
function _barrerHuerfanos() {
    vivos.forEach(function (c) {
        if (c.section.isConnected === false) _desmontar(c, {});
    });
}

// ── Observadores y presupuesto ─────────────────────────────────────────────────────────────────

function _arrancar() {
    if (enMarcha) return;
    enMarcha = true;
    if (typeof IntersectionObserver === 'function') {
        io = new IntersectionObserver(_alCambiarVisibilidad, { threshold: UMBRALES });
    }
    if (typeof ResizeObserver === 'function') {
        ro = new ResizeObserver(_alCambiarTamano);
    }
    document.addEventListener('visibilitychange', _replanificar);
}

function _alCambiarVisibilidad(entradas) {
    entradas.forEach(function (e) {
        const c = porSeccion.get(e.target);
        if (c) c.visible = !!e.isIntersecting;
    });
    _replanificar();
}

function _alCambiarTamano(entradas) {
    entradas.forEach(function (e) {
        const c = porSeccion.get(e.target);
        if (!c || !c.efecto || c.destruido) return;
        const m = _medida(c);
        if (c.medida && m.w === c.medida.w && m.h === c.medida.h) return;
        c.medida = m;
        // Re-mide la caja SIN reiniciar el estado del efecto (E6): el lienzo sigue a la sección.
        _protegido(c, function () { c.efecto.redimensionar(); });
        // Redimensionar un canvas lo deja en blanco: si no está animando, se repinta un fotograma.
        c.pintado = false;
        if (!c.animando) _quieto(c);
    });
}

/** La caja que llena el efecto (la de su capa), en px CSS de MAQUETA: una animación de entrada con
 *  `transform` cambia la caja que se ve, no la que se pinta, y no dispara el ResizeObserver. */
function _medida(c) {
    return { w: c.layer.clientWidth, h: c.layer.clientHeight };
}

/** Una sola planificación por tarea aunque lleguen varios avisos (montajes, IO, RO…). */
function _replanificar() {
    if (planPendiente) return;
    planPendiente = true;
    Promise.resolve().then(function () {
        planPendiente = false;
        _planificar();
    });
}

function _planificar() {
    _barrerHuerfanos();
    const oculto = typeof document !== 'undefined' && !!document.hidden;
    const quietos = fondosQuietos() || oculto;
    const candidatos = [];
    vivos.forEach(function (c) {
        // Un efecto que ha fallado se queda en su último fotograma.
        if (!c.efecto || c.roto) return;
        candidatos.push({ id: c, visible: c.visible, preview: c.preview, animando: c.animando,
                          area: c.visible ? _areaVisible(c) : 0 });
    });
    const animan = quietos ? [] : elegirAnimados(candidatos, PRESUPUESTO);
    candidatos.forEach(function (k) {
        if (animan.indexOf(k.id) !== -1) _animar(k.id);
        else _quieto(k.id);
    });
}

function _animar(c) {
    if (c.animando) return;
    c.animando = true;
    c.pintado = true;
    _protegido(c, function () { c.efecto.animar(); });
    if (c.efecto.pintar) _pedirBucle();
}

// ── El bucle único de los lienzos ──────────────────────────────────────────────────────────────

function _pedirBucle() {
    if (bucle === null && typeof requestAnimationFrame === 'function') bucle = requestAnimationFrame(_bucle);
}

/**
 * UNA vuelta del rAF para todos los lienzos que animan. Sigue pidiendo fotogramas mientras quede
 * alguno; pinta solo cuando le toca a la cadencia (30 fps con corrección de fase: a 60, 120 o 144 Hz
 * igual) y le pasa a cada efecto el `dt` real (acotado tras una pausa). Cuando no anima ninguno, se
 * para y la cadencia se reinicia: al volver, el primer fotograma da un paso nominal.
 */
function _bucle(ahora) {
    bucle = null;
    const lienzos = [];
    vivos.forEach(function (c) {
        if (c.animando && c.efecto && c.efecto.pintar && !c.roto) lienzos.push(c);
    });
    if (!lienzos.length) {
        cadencia.reiniciar();
        return;
    }
    bucle = requestAnimationFrame(_bucle);
    const dt = cadencia.paso(ahora);
    if (dt < 0) return;
    lienzos.forEach(function (c) {
        try { c.efecto.pintar(dt); }
        catch (err) {
            // Se queda en su último fotograma, sin tumbar a los demás (y sin reintentar cada vuelta).
            _avisar(c.tipo, err);
            c.roto = true;
            c.animando = false;
        }
    });
}

/** Quieto: parado y, si se ve y no tiene nada pintado, con un fotograma. */
function _quieto(c) {
    if (c.animando) {
        c.animando = false;
        _protegido(c, function () { c.efecto.parar(); });
    }
    if (!c.pintado && c.visible && !(typeof document !== 'undefined' && document.hidden)) {
        c.pintado = true;
        _protegido(c, function () { c.efecto.fotograma(); });
    }
}

/** Píxeles visibles de la sección en la ventana. */
function _areaVisible(c) {
    const r = c.section.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth || 0;
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    const w = Math.max(0, Math.min(r.right, vw) - Math.max(r.left, 0));
    const h = Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0));
    return w * h;
}

// ── Utilidades ─────────────────────────────────────────────────────────────────────────────────

function _ambito(raiz) {
    return (raiz && typeof raiz.querySelectorAll === 'function') ? raiz : document;
}

/** ¿Está esta sección dentro del ámbito? */
function _enAmbito(section, ambito) {
    if (!ambito || ambito === document) return true;
    return section === ambito || (typeof ambito.contains === 'function' && ambito.contains(section));
}

/** Un efecto que lanza se queda quieto (en su último fotograma) y no tumba a los demás. */
function _protegido(c, fn) {
    try { fn(); }
    catch (err) { _avisar(c.tipo, err); }
}

/** Avisa UNA vez por tipo de fondo. */
function _avisar(tipo, err) {
    if (avisados.has(tipo)) return;
    avisados.add(tipo);
    console.warn('[fondos] el fondo «' + tipo + '» ha fallado y se queda quieto:', err);
}
